import {
  AppShell,
  Button,
  ActionIcon,
  TextInput,
  Select,
  Modal,
  Notification,
  MantineProvider as BaseMantineProvider,
  MantineColorsTuple,
  CSSVariablesResolver,
  ColorSchemeScript,
  createTheme,
  localStorageColorSchemeManager,
} from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import '@mantine/core/styles.layer.css';
import '@mantine/notifications/styles.layer.css';
// @mantine/modals doesn't have separate styles - uses core styles
import '@mantine/spotlight/styles.layer.css';
import '@mantine/dropzone/styles.layer.css';
import '@mantine/dates/styles.layer.css';
import '@mantine/charts/styles.layer.css';
import '@mantine/code-highlight/styles.layer.css';
import '@mantine/tiptap/styles.layer.css';

// Fonterra brand colors
const fonterraBlue: MantineColorsTuple = [
  '#e6f3ff',
  '#cce7ff',
  '#99cfff',
  '#66b7ff',
  '#339fff',
  '#0087ff', // Primary brand blue
  '#006fcc',
  '#005799',
  '#003f66',
  '#002733',
];

const fonterraTeal: MantineColorsTuple = [
  '#e6f9f7',
  '#ccf3ef',
  '#99e7df',
  '#66dbcf',
  '#33cfbf',
  '#00c3af', // Secondary teal
  '#009c8c',
  '#007569',
  '#004e46',
  '#002723',
];

const colorSchemeManager = localStorageColorSchemeManager({
  key: 'fonterra-color-scheme',
});

const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    '--fonterra-gradient-start': '#1D4DA4',
    '--fonterra-gradient-end': '#59B836',
    '--fonterra-shadow-popover': '0px 0px 12px 0px rgba(0, 0, 0, 0.24)',
    '--fonterra-toggle-thumb': '#FFFFFF',
    '--fonterra-toggle-icon-on': '#59B836',
    '--fonterra-toggle-icon-off': '#7E899D',
    '--fonterra-toggle-icon': 'var(--fonterra-toggle-icon-off)',
    '--fonterra-text-on-accent': '#FFFFFF',
    '--fonterra-danger-primary': '#FF4A4A',
    '--fonterra-danger-primary-hover': '#CC3B3B',
    '--fonterra-scroll-fade-start': 'rgba(255, 255, 255, 0.85)',
    '--fonterra-scroll-fade-end': 'rgba(255, 255, 255, 1)',
    '--fonterra-scrollbar-track': 'rgba(0, 0, 0, 0.08)',
    '--fonterra-scrollbar-thumb': 'rgba(0, 0, 0, 0.18)',
    '--fonterra-scrollbar-thumb-hover': 'rgba(0, 0, 0, 0.28)',
    '--fonterra-card-shadow': '0px 1px 3px rgba(0, 0, 0, 0.1)',
    '--fonterra-card-shadow-hover': '0px 2px 8px rgba(0, 0, 0, 0.15)',
    '--fonterra-button-secondary-bg': '#FFFFFF',
    '--fonterra-button-secondary-border': 'none',
    '--fonterra-button-secondary-text': '#001B66',
    '--fonterra-button-secondary-hover-bg': '#E6E9ED',
    '--fonterra-button-secondary-hover-text': '#001B66',
    '--fonterra-button-secondary-disabled-bg': '#F5F6F7',
    '--fonterra-button-secondary-disabled-text': '#8394A3',
    '--fonterra-profile-button-bg': '#008181',
    '--fonterra-profile-button-hover-bg': '#006666',
    '--fonterra-profile-button-border': 'transparent',
    '--fonterra-profile-button-text': '#FFFFFF',
    '--fonterra-quickstart-bg': '#FFFFFF',
    '--fonterra-quickstart-hover-bg': '#F8FAFB',
    '--fonterra-quickstart-shadow': '0px 1px 3px rgba(0, 0, 0, 0.1)',
    '--fonterra-quickstart-shadow-hover': '0px 2px 8px rgba(0, 0, 0, 0.15)',
    '--fonterra-quickstart-hover-border': '#CCD2DB',
    '--fonterra-promptlibrary-modal-border': '#2A80F7',
    '--fonterra-promptlibrary-tab-active-bg': '#2A80F7',
    '--fonterra-promptlibrary-tab-active-color': '#FFFFFF',
    '--fonterra-promptlibrary-card-focus-outline': '#2A80F7',
    '--fonterra-agent-modal-border-radius': '9px',
  },
  light: {
    '--fonterra-surface-base': '#FFFFFF',
    '--fonterra-surface-raised': '#FFFFFF',
    '--fonterra-surface-subtle': '#F3F6FB',
    '--fonterra-surface-popover': '#FFFFFF',
    '--fonterra-surface-input': '#F1F2F5',
    '--fonterra-text-primary': '#2A2A2A',
    '--fonterra-text-secondary': '#3E4C5E',
    '--fonterra-text-muted': '#7E899D',
    '--fonterra-border-default': '#CCD2DB',
    '--fonterra-border-strong': '#A3ABBB',
    '--fonterra-border-accent': '#59B836',
    '--fonterra-hover-muted': '#E6E9ED',
    '--fonterra-toggle-track-off': '#A3ABBB',
    '--fonterra-toggle-track-on': '#59B836',
    '--fonterra-modal-cancel-text': '#001B66',
    '--fonterra-modal-cancel-hover-bg': 'rgba(0, 27, 102, 0.1)',
    '--fonterra-modal-cancel-hover-text': '#001B66',
    '--fonterra-accent-positive-hover': '#4A9E2E',
    '--fonterra-accent-positive-disabled': '#59B836',
    '--fonterra-card-border-hover': '#59B836',
    '--fonterra-quickstart-border': '#E1E4E8',
    '--fonterra-quickstart-bg': '#FFFFFF',
    '--fonterra-quickstart-text': '#000000',
    '--fonterra-quickstart-label': '#7E899D',
    '--fonterra-bubble-assistant-bg': '#FFFFFF',
    '--fonterra-bubble-assistant-border': '#CCD2DB',
    '--fonterra-bubble-user-bg': '#F1F2F5',
    '--fonterra-bubble-user-border': '#E1E4E8',
    '--fonterra-bubble-text': '#2A2A2A',
    '--fonterra-input-border': '#CCD2DB',
    '--fonterra-input-border-focus': '#59B836',
    '--fonterra-input-placeholder': '#ADB5BD',
    '--fonterra-nav-search-placeholder': 'var(--fonterra-input-placeholder)',
    '--fonterra-input-text': '#2A2A2A',
    '--fonterra-icon-muted': '#616D83',
    '--fonterra-icon-strong': '#2A2A2A',
    '--fonterra-icon-hover-bg': '#F8F9FA',
    '--fonterra-accent-primary': '#2A80F7',
    '--fonterra-citation-bg': 'rgba(42, 128, 247, 0.05)',
    '--fonterra-citation-border': 'rgba(42, 128, 247, 0.2)',
    '--fonterra-citation-bg-hover': 'rgba(42, 128, 247, 0.1)',
    '--fonterra-citation-border-hover': 'rgba(42, 128, 247, 0.3)',
    '--fonterra-citation-text': '#2A80F7',
    '--fonterra-streaming-cursor': '#2A80F7',
    '--fonterra-attachment-bg': '#FFFFFF',
    '--fonterra-attachment-bg-hover': '#FFFFFF',
    '--fonterra-attachment-border': '#CCD2DB',
    '--fonterra-attachment-icon-bg': '#F1F2F5',
    '--fonterra-attachment-text': '#2A2A2A',
    '--fonterra-attachment-subtext': '#7E899D',
    '--fonterra-attachment-action': '#616D83',
    '--fonterra-attachment-action-hover': '#2A80F7',
    '--fonterra-attachment-action-hover-bg': 'rgba(42, 128, 247, 0.05)',
    '--fonterra-promptlibrary-modal-bg': '#FFFFFF',
    '--fonterra-promptlibrary-tab-bg': '#EAF3FF',
    '--fonterra-promptlibrary-tab-border': 'none',
    '--fonterra-promptlibrary-tab-color': '#2A80F7',
    '--fonterra-promptlibrary-tab-hover-bg': '#94BFFA',
    '--fonterra-promptlibrary-tab-hover-border': 'none',
    '--fonterra-promptlibrary-tab-hover-color': '#2A80F7',
    '--fonterra-promptlibrary-tab-active-border': 'none',
    '--fonterra-promptlibrary-card-bg': '#FFFFFF',
    '--fonterra-promptlibrary-card-border': '#CCD2DB',
    '--fonterra-promptlibrary-card-hover-bg': '#E6E9ED',
    '--fonterra-promptlibrary-card-hover-border': '#CCD2DB',
    '--fonterra-promptlibrary-author-color': '#616D83',
    '--fonterra-promptlibrary-delete-color': '#616D83',
    '--fonterra-promptlibrary-delete-hover-bg': '#CCD2DB',
    '--fonterra-promptlibrary-delete-hover-color': '#616D83',
    '--fonterra-agent-modal-bg': '#FFFFFF',
    '--fonterra-agent-modal-section-label': '#616D83',
    '--fonterra-agent-modal-prompt-bg': '#FFFFFF',
    '--fonterra-agent-modal-prompt-border': '#CCD2DB',
    '--fonterra-agent-modal-prompt-hover-bg': '#E6E9ED',
    '--fonterra-agent-modal-prompt-hover-border': '#CCD2DB',
    '--fonterra-agent-modal-prompt-text': '#2A2A2A',
    '--fonterra-agent-modal-capability-bg': 'rgba(42, 128, 247, 0.05)',
    '--fonterra-agent-modal-capability-border': 'rgba(42, 128, 247, 0.2)',
    '--fonterra-agent-modal-capability-hover-bg': 'rgba(42, 128, 247, 0.1)',
    '--fonterra-agent-modal-capability-hover-border': 'rgba(42, 128, 247, 0.3)',
    '--fonterra-agent-modal-capability-text': '#2A80F7',
    '--fonterra-agent-modal-pin-text': '#001B66',
    '--fonterra-agent-modal-pin-icon': '#001B66',
    '--fonterra-agent-modal-pin-hover-bg': '#E6E9ED',
    '--fonterra-agent-modal-close-icon': '#616D83',
    '--fonterra-agent-modal-primary-bg': '#59B836',
    '--fonterra-agent-modal-primary-hover-bg': '#47932B',
    '--fonterra-agent-card-hover-bg': '#E6E9ED',
    '--fonterra-agent-card-hover-border': '#CCD2DB',
    '--fonterra-agent-card-hover-title': '#2A2A2A',
    '--fonterra-agent-card-hover-desc': '#2A2A2A',
    '--fonterra-agent-card-hover-icon': '#59B836',
    '--fonterra-search-hover-bg': '#E6E9ED',
    '--fonterra-search-hover-border': '#CCD2DB',
    '--fonterra-search-hover-placeholder': '#A3ABBB',
    '--fonterra-title-hover-bg': '#E6E9ED',
    '--fonterra-title-hover-border': '#CCD2DB',
  },
  dark: {
    '--fonterra-surface-base': '#2D3440',
    '--fonterra-surface-raised': '#384153',
    '--fonterra-surface-subtle': '#384153',
    '--fonterra-surface-popover': '#292B2F',
    '--fonterra-surface-input': '#384153',
    '--fonterra-text-primary': '#FFFFFF',
    '--fonterra-text-secondary': '#A3ABBB',
    '--fonterra-text-muted': '#7E899D',
    '--fonterra-border-default': '#495469',
    '--fonterra-border-strong': '#616D83',
    '--fonterra-border-accent': '#59B836',
    '--fonterra-hover-muted': 'rgba(117, 214, 255, 0.12)',
    '--fonterra-toggle-track-off': '#495469',
    '--fonterra-toggle-track-on': '#59B836',
    '--fonterra-nav-search-placeholder': '#FFFFFF80',
    '--fonterra-accent-positive-hover': '#4A9E2E',
    '--fonterra-accent-positive-disabled': '#59B836',
    '--fonterra-card-border-hover': '#59B836',
    '--fonterra-quickstart-border': '#495469',
    '--fonterra-quickstart-bg': '#2D3440',
    '--fonterra-quickstart-hover-bg': '#384153',
    '--fonterra-quickstart-text': 'rgba(255, 255, 255, 0.9)',
    '--fonterra-quickstart-shadow': 'none',
    '--fonterra-quickstart-shadow-hover': 'none',
    '--fonterra-quickstart-label': '#A3ABBB',
    '--fonterra-quickstart-hover-border': '#495469',
    '--fonterra-bubble-assistant-bg': '#384153',
    '--fonterra-bubble-assistant-border': 'rgba(255, 255, 255, 0)',
    '--fonterra-bubble-user-bg': '#2D3440',
    '--fonterra-bubble-user-border': '#495469',
    '--fonterra-bubble-text': '#FFFFFF',
    '--fonterra-input-border': '#495469',
    '--fonterra-input-border-focus': '#59B836',
    '--fonterra-input-placeholder': 'rgba(255, 255, 255, 0.5)',
    '--fonterra-input-text': '#FFFFFF',
    '--fonterra-icon-muted': '#7E899D',
    '--fonterra-icon-strong': '#FFFFFF',
    '--fonterra-icon-hover-bg': 'rgba(255, 255, 255, 0.08)',
    '--fonterra-accent-primary': '#75D6FF',
    '--fonterra-citation-bg': 'rgba(42, 128, 247, 0.1)',
    '--fonterra-citation-border': 'rgba(42, 128, 247, 0.2)',
    '--fonterra-citation-bg-hover': 'rgba(42, 128, 247, 0.16)',
    '--fonterra-citation-border-hover': 'rgba(42, 128, 247, 0.32)',
    '--fonterra-citation-text': '#75D6FF',
    '--fonterra-streaming-cursor': '#75D6FF',
    '--fonterra-attachment-bg': '#495469',
    '--fonterra-attachment-bg-hover': '#495469',
    '--fonterra-attachment-border': '#616D83',
    '--fonterra-attachment-icon-bg': 'transparent',
    '--fonterra-attachment-text': '#FFFFFF',
    '--fonterra-attachment-subtext': '#A3ABBB',
    '--fonterra-attachment-action': '#A3ABBB',
    '--fonterra-attachment-action-hover': '#75D6FF',
    '--fonterra-attachment-action-hover-bg': 'rgba(117, 214, 255, 0.12)',
    '--fonterra-promptlibrary-modal-bg': '#292B2F',
    '--fonterra-promptlibrary-tab-bg': 'rgba(42, 128, 247, 0.2)',
    '--fonterra-promptlibrary-tab-border': '1px solid rgba(42, 128, 247, 0.2)',
    '--fonterra-promptlibrary-tab-color': '#2A80F7',
    '--fonterra-promptlibrary-tab-hover-bg': 'rgba(42, 128, 247, 0.32)',
    '--fonterra-promptlibrary-tab-hover-border': '1px solid rgba(42, 128, 247, 0.32)',
    '--fonterra-promptlibrary-tab-hover-color': '#FFFFFF',
    '--fonterra-promptlibrary-tab-active-border': '1px solid #2A80F7',
    '--fonterra-promptlibrary-card-bg': '#2D3440',
    '--fonterra-promptlibrary-card-border': '#495469',
    '--fonterra-promptlibrary-card-hover-bg': '#384153',
    '--fonterra-promptlibrary-card-hover-border': '#495469',
    '--fonterra-promptlibrary-author-color': '#A3ABBB',
    '--fonterra-promptlibrary-delete-color': '#A3ABBB',
    '--fonterra-promptlibrary-delete-hover-bg': 'rgba(42, 128, 247, 0.12)',
    '--fonterra-promptlibrary-delete-hover-color': '#FFFFFF',
    '--fonterra-agent-modal-bg': '#292B2F',
    '--fonterra-agent-modal-section-label': '#7E899D',
    '--fonterra-agent-modal-prompt-bg': '#2D3440',
    '--fonterra-agent-modal-prompt-border': '#495469',
    '--fonterra-agent-modal-prompt-hover-bg': '#384153',
    '--fonterra-agent-modal-prompt-hover-border': '#616D83',
    '--fonterra-agent-modal-prompt-text': 'rgba(255, 255, 255, 0.9)',
    '--fonterra-agent-modal-capability-bg': 'rgba(42, 128, 247, 0.1)',
    '--fonterra-agent-modal-capability-border': 'rgba(42, 128, 247, 0.2)',
    '--fonterra-agent-modal-capability-hover-bg': 'rgba(42, 128, 247, 0.16)',
    '--fonterra-agent-modal-capability-hover-border': 'rgba(42, 128, 247, 0.32)',
    '--fonterra-agent-modal-capability-text': '#75D6FF',
    '--fonterra-agent-modal-pin-text': '#CCD2DB',
    '--fonterra-agent-modal-pin-icon': '#FFFFFF',
    '--fonterra-agent-modal-pin-hover-bg': 'rgba(255, 255, 255, 0.08)',
    '--fonterra-agent-modal-close-icon': '#FFFFFF',
    '--fonterra-agent-modal-primary-bg': '#47932B',
    '--fonterra-agent-modal-primary-hover-bg': '#59B836',
    '--fonterra-agent-card-hover-bg': '#495469',
    '--fonterra-agent-card-hover-border': '#616D83',
    '--fonterra-agent-card-hover-title': '#FFFFFF',
    '--fonterra-agent-card-hover-desc': 'rgba(255, 255, 255, 0.9)',
    '--fonterra-agent-card-hover-icon': '#59B836',
    '--fonterra-search-hover-bg': '#495469',
    '--fonterra-search-hover-border': '#616D83',
    '--fonterra-search-hover-placeholder': '#CCD2DB',
    '--fonterra-title-hover-bg': '#495469',
    '--fonterra-title-hover-border': '#616D83',
    '--fonterra-button-secondary-bg': 'transparent',
    '--fonterra-button-secondary-border': 'none',
    '--fonterra-button-secondary-text': 'var(--fonterra-text-primary)',
    '--fonterra-button-secondary-hover-bg': 'var(--fonterra-hover-muted)',
    '--fonterra-button-secondary-hover-text': 'var(--fonterra-text-primary)',
    '--fonterra-button-secondary-disabled-bg': 'transparent',
    '--fonterra-button-secondary-disabled-text': 'var(--fonterra-text-muted)',
    '--fonterra-modal-cancel-text': 'var(--fonterra-text-primary)',
    '--fonterra-modal-cancel-hover-bg': '#3E4043',
    '--fonterra-modal-cancel-hover-text': '#FFFFFF',
    '--fonterra-profile-button-bg': 'var(--fonterra-surface-subtle)',
    '--fonterra-profile-button-hover-bg': 'var(--fonterra-hover-muted)',
    '--fonterra-profile-button-border': '1px solid var(--fonterra-border-default)',
    '--fonterra-profile-button-text': 'var(--fonterra-text-primary)',
    '--fonterra-scroll-fade-start': 'rgba(45, 52, 64, 0.65)',
    '--fonterra-scroll-fade-end': 'rgba(45, 52, 64, 1)',
    '--fonterra-scrollbar-track': 'rgba(255, 255, 255, 0.1)',
    '--fonterra-scrollbar-thumb': 'rgba(255, 255, 255, 0.32)',
    '--fonterra-scrollbar-thumb-hover': 'rgba(255, 255, 255, 0.5)',
    '--fonterra-card-shadow': '0px 8px 24px rgba(8, 12, 24, 0.45)',
    '--fonterra-card-shadow-hover': '0px 12px 32px rgba(8, 12, 24, 0.55)',
  },
});

// Create Mantine theme matching Fonterra design system
const theme = createTheme({
  primaryColor: 'fonterra-blue',
  colors: {
    'fonterra-blue': fonterraBlue,
    'fonterra-teal': fonterraTeal,
  },
  fontFamily: 'Segoe UI, -apple-system, BlinkMacSystemFont, sans-serif',
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, monospace',
  headings: {
    fontFamily: 'Segoe UI, -apple-system, BlinkMacSystemFont, sans-serif',
    sizes: {
      h1: { fontSize: '2rem', lineHeight: '1.2' },
      h2: { fontSize: '1.75rem', lineHeight: '1.3' },
      h3: { fontSize: '1.5rem', lineHeight: '1.4' },
      h4: { fontSize: '1.25rem', lineHeight: '1.4' },
      h5: { fontSize: '1.125rem', lineHeight: '1.5' },
      h6: { fontSize: '1rem', lineHeight: '1.5' },
    },
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  radius: {
    xs: '0.125rem',
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
  },
  shadows: {
    xs: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    sm: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  },
  components: {
    Button: Button.extend({
      defaultProps: {
        size: 'sm',
        radius: 'md',
      },
      styles: {
        root: {
          fontWeight: 500,
          transition: 'all 150ms ease',
        },
      },
    }),

    ActionIcon: ActionIcon.extend({
      defaultProps: {
        variant: 'subtle',
        radius: 'md',
      },
      styles: {
        root: {
          transition: 'all 150ms ease',
        },
      },
    }),

    TextInput: TextInput.extend({
      defaultProps: {
        size: 'sm',
        radius: 'md',
      },
      styles: {
        input: {
          transition: 'border-color 150ms ease',
          '&:focus': {
            borderColor: 'var(--mantine-color-fonterra-blue-6)',
          },
        },
      },
    }),

    Select: Select.extend({
      defaultProps: {
        size: 'sm',
        radius: 'md',
      },
    }),

    Modal: Modal.extend({
      defaultProps: {
        radius: 'md',
        shadow: 'lg',
      },
    }),

    Notification: Notification.extend({
      defaultProps: {
        radius: 'md',
      },
    }),

    AppShell: AppShell.extend({
      styles: {
        main: {
          transition: 'padding-left 200ms ease',
        },
      },
    }),
  },
});

interface MantineProviderProps {
  children: React.ReactNode;
}

export function MantineProvider({ children }: MantineProviderProps) {
  return (
    <>
      <ColorSchemeScript defaultColorScheme="light" />
      <BaseMantineProvider
        theme={theme}
        defaultColorScheme="light"
        colorSchemeManager={colorSchemeManager}
        cssVariablesResolver={cssVariablesResolver}
      >
        <ModalsProvider>
          <Notifications position="top-right" />
          {children}
        </ModalsProvider>
      </BaseMantineProvider>
    </>
  );
}
